import {
  createBrandPostTranslationHandler,
  replaceProtectasBrand,
  resolveProtectasBrand,
} from '~/utils/brandTranslation'

export default defineNuxtPlugin(async (nuxtApp) => {
  const { setupUserLanguage } = useLanguageSetup()
  const { translations } = storeToRefs(useLanguageStore())
  const { isProtectasUser } = storeToRefs(useAuthStore())
  const webReportStore = useWebReportStore()

  const shouldSubstituteBrand = () =>
    resolveProtectasBrand(
      webReportStore.activeReportProtectasBrand,
      isProtectasUser.value,
    )

  // Every `$t` in the app rebrands through this handler. It has to be
  // registered here rather than declared in `i18n/i18n.config.ts`: the config
  // is evaluated during i18n setup, so a handler declared there would have to
  // resolve Pinia lazily on every call, whereas this plugin already holds the
  // live store refs that `$trp` uses.
  nuxtApp.$i18n.setPostTranslationHandler(
    createBrandPostTranslationHandler(shouldSubstituteBrand),
  )

  const isPublicWebReport
    = import.meta.client
      && window.location.pathname.startsWith('/web-report/')

  if (!isPublicWebReport && window.location.pathname !== '/') {
    await setupUserLanguage()
  }
  // watch for userContext to be available or changed and setup user language
  nuxtApp.hooks.hook('app:mounted', async () => {
    if (
      import.meta.client
        && window.location.pathname.startsWith('/web-report/')
    ) {
      return
    }
    const authStore = useAuthStore()
    const { userContext } = storeToRefs(authStore)
    watch(
      () => userContext.value,
      async () => {
        await setupUserLanguage()
      },
    )
  })

  // provide translation function globally
  return {
    provide: {
      tx: (msg?: string | null) => {
        if (!msg)
          return ''
        const translatedText
          = translations.value instanceof Map
            ? (translations.value.get(msg) ?? msg)
            : msg
        return shouldSubstituteBrand()
          ? (replaceProtectasBrand(translatedText) ?? translatedText)
          : translatedText
      },
      isProtectasUser,
      trp: (text?: string) => {
        return shouldSubstituteBrand()
          ? (replaceProtectasBrand(text) ?? text ?? '')
          : (text ?? '')
      },
    },
  }
})
